import axios, { AxiosError, InternalAxiosRequestConfig } from "axios";
import { API_URL } from "@/config";
import axiosCallCenter from "@/services/call-center/axios.callcenter";
import axiosCoachingAI from "@/services/axios-coaching-ai";
import { getUserTimezone } from "./dates";
import { refreshAccessToken } from "@/services/auth.service";
import { PATH_LOGIN } from "@/routes/paths";

const axiosInstance = axios.create({
  baseURL: API_URL,
});

axiosInstance.interceptors.request.use((config) => {
  config.headers['X-Timezone'] = getUserTimezone();
  return config;
});

// ---- Token management ----

export const setAuthToken = (accessToken?: string) => {
  const token = accessToken || localStorage.getItem("accessToken");
  if (!token) return;
  localStorage.setItem("accessToken", token);
  axiosInstance.defaults.headers.common["Authorization"] = `Bearer ${token}`;
  axiosCallCenter.defaults.headers.common["Authorization"] = `Bearer ${token}`;
  axiosCoachingAI.defaults.headers.common["Authorization"] = `Bearer ${token}`;
};

export const setRefreshToken = (refreshToken: string) => {
  localStorage.setItem("refreshToken", refreshToken);
};

export const setAuthProvider = (provider: string) => {
  localStorage.setItem("authProvider", provider);
};

export const getAuthToken = () => localStorage.getItem("accessToken");
export const getRefreshToken = () => localStorage.getItem("refreshToken");
export const getAuthProvider = () => localStorage.getItem("authProvider");

export const removeAuthToken = () => {
  localStorage.removeItem("accessToken");
  localStorage.removeItem("refreshToken");
  localStorage.removeItem("authProvider");
  localStorage.clear();
  sessionStorage.clear();
};

// ---- Refresh token interceptor ----

type QueueItem = {
  resolve: (token: string) => void;
  reject: (error: unknown) => void;
};

let isRefreshing = false;
let failedQueue: QueueItem[] = [];

const processQueue = (error: unknown, token: string | null) => {
  failedQueue.forEach(({ resolve, reject }) => {
    if (token) {
      resolve(token);
    } else {
      reject(error);
    }
  });
  failedQueue = [];
};

const forceLogout = () => {
  removeAuthToken();
  window.location.href = PATH_LOGIN;
};

const createResponseInterceptor = (instance: typeof axiosInstance) => {
  instance.interceptors.response.use(
    (response) => response,
    async (error: AxiosError) => {
      const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean };

      if (!originalRequest || error.response?.status !== 401 || originalRequest._retry) {
        return Promise.reject(error);
      }

      // Don't intercept auth endpoints to avoid loops
      if (originalRequest.url?.includes("/auth/refresh") || originalRequest.url === "/login") {
        return Promise.reject(error);
      }

      const storedRefreshToken = getRefreshToken();
      if (!storedRefreshToken) {
        forceLogout();
        return Promise.reject(error);
      }

      if (isRefreshing) {
        return new Promise<string>((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        }).then((newToken) => {
          originalRequest.headers["Authorization"] = `Bearer ${newToken}`;
          return instance(originalRequest);
        });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        const { AccessToken } = await refreshAccessToken(storedRefreshToken);
        setAuthToken(AccessToken);
        processQueue(null, AccessToken);
        originalRequest.headers["Authorization"] = `Bearer ${AccessToken}`;
        return instance(originalRequest);
      } catch (refreshError) {
        processQueue(refreshError, null);
        forceLogout();
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }
  );
};

createResponseInterceptor(axiosInstance);
createResponseInterceptor(axiosCallCenter);
createResponseInterceptor(axiosCoachingAI);

export default axiosInstance;
